import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocs, collectionGroup } from 'firebase/firestore';
import { Users, Search, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import TeamFlag from '../../components/ui/TeamFlag';
import { db } from '../../firebase/config';

import { useAppContext } from '../../context/AppContext';
import { getLastMonthRating } from '../../utils/calculations';
import { normalizeCity } from '../../utils/geo';
import logger from '../../utils/logger';
import Header from '../../components/layout/Header';
import CityAutocomplete from '../../components/ui/CityAutocomplete';
import PositionBadge from '../../components/ui/PositionBadge';
import PlayerAvatar from '../../components/ui/PlayerAvatar';

const SORT_OPTIONS = [
  { id: 'jogos', label: 'Jogos' },
  { id: 'gols', label: 'Gols' },
  { id: 'assistencias', label: 'Assist.' },
  { id: 'galera', label: 'Nota Galera' },
  { id: 'torcida', label: 'Por Torcida' },
];

const POSITION_OPTIONS = [
  { id: '', label: 'Todas' },
  { id: 'Goleiro', label: 'Goleiro' },
  { id: 'Defensor', label: 'Defensor' },
  { id: 'Meia', label: 'Meia' },
  { id: 'Atacante', label: 'Atacante' },
];

const CommunityPage = () => {
  const { groups, publicGroups } = useAppContext();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('jogos');
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [cityFilter, setCityFilter] = useState('');
  const [period, setPeriod] = useState('all');
  const [positionFilter, setPositionFilter] = useState('');

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const snap = await getDocs(collectionGroup(db, 'profile'));
        const data = snap.docs
          .filter(d => d.id === 'data' && d.data().name)
          .map(d => ({ id: d.ref.parent.parent.id, ...d.data() }));
        setProfiles(data);
      } catch (err) {
        logger.error('Erro ao carregar comunidade:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, []);

  const ranked = useMemo(() => {
    const withStats = profiles.map(p => {
      const hasComm = p.communityRating !== null && p.communityRating !== undefined;
      const monthRating = getLastMonthRating(p);
      const effectiveRating = period === 'month' ? (monthRating ?? (hasComm ? p.communityRating : null)) : (hasComm ? p.communityRating : null);
      return {
        ...p,
        gameCount: p.gamesPlayed ?? (p.ratingHistory?.length || 0),
        goalsCount: p.totalGoals || 0,
        assistsCount: p.totalAssists || 0,
        communityRating: effectiveRating,
        manualRating: p.manualRating || 5,
        isMonthFallback: period === 'month' && monthRating === null && hasComm,
      };
    });
    if (sortBy === 'galera') return [...withStats].sort((a, b) => (b.communityRating ?? -1) - (a.communityRating ?? -1) || b.gameCount - a.gameCount);
    if (sortBy === 'manual') return [...withStats].sort((a, b) => b.manualRating - a.manualRating || b.gameCount - a.gameCount);
    if (sortBy === 'gols') return [...withStats].sort((a, b) => b.goalsCount - a.goalsCount || b.gameCount - a.gameCount);
    if (sortBy === 'assistencias') return [...withStats].sort((a, b) => b.assistsCount - a.assistsCount || b.gameCount - a.gameCount);
    return [...withStats].sort((a, b) => b.gameCount - a.gameCount || (b.communityRating ?? -1) - (a.communityRating ?? -1));
  }, [profiles, sortBy, period]);

  // Mapear userId → cidades dos grupos que participa
  const userCities = useMemo(() => {
    const map = {};
    [...groups, ...publicGroups].forEach(g => {
      if (!g.city) return;
      (g.memberIds || []).forEach(uid => {
        if (!map[uid]) map[uid] = new Set();
        map[uid].add(g.city);
      });
    });
    return map;
  }, [groups, publicGroups]);


  const filtered = useMemo(() => {
    let list = ranked;
    if (cityFilter.trim()) {
      const nf = normalizeCity(cityFilter);
      list = list.filter(p => {
        // 1. cidade do próprio perfil
        if (p.city && normalizeCity(p.city) === nf) return true;
        // 2. fallback: cidades dos grupos que participa
        const groupCities = userCities[p.id];
        if (groupCities) {
          for (const c of groupCities) {
            if (normalizeCity(c) === nf) return true;
          }
        }
        return false;
      });
    }
    if (search.trim()) list = list.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()));
    if (positionFilter) list = list.filter(p => p.position === positionFilter);
    return list;
  }, [ranked, cityFilter, search, userCities, positionFilter]);

  const torcidaRanking = useMemo(() => {
    const base = filtered; // usa filtered para aplicar todos os filtros ativos (cidade, posição, busca)
    const map = {};
    base.forEach(p => {
      if (!p.heartTeam || p.heartTeam === 'Sem Time') return;
      if (!map[p.heartTeam]) map[p.heartTeam] = { team: p.heartTeam, players: [], ratingSum: 0, ratingCount: 0 };
      map[p.heartTeam].players.push(p);
      if (p.communityRating != null) { map[p.heartTeam].ratingSum += p.communityRating; map[p.heartTeam].ratingCount++; }
    });
    return Object.values(map)
      .map(e => ({ ...e, count: e.players.length, avgRating: e.ratingCount > 0 ? e.ratingSum / e.ratingCount : null }))
      .sort((a, b) => b.count - a.count || (b.avgRating ?? 0) - (a.avgRating ?? 0));
  }, [filtered]);

  return (
    <>
      <Header />
      <div className="p-6 space-y-5 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Comunidade</h2>
            {!loading && (
              <p className="text-sm text-slate-400 mt-0.5">
                <span className="font-bold text-blue-600">{cityFilter ? filtered.length : profiles.length}</span> jogador{(cityFilter ? filtered.length : profiles.length) !== 1 ? 'es' : ''}{cityFilter ? ` em ${cityFilter}` : ' cadastrados'}
              </p>
            )}
          </div>
          <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center">
            <Users className="w-6 h-6 text-blue-500" />
          </div>
        </div>

        {/* Filtro de cidade + Busca por jogador */}
        <div className="space-y-2">
          <CityAutocomplete
            value={cityFilter}
            onChange={val => setCityFilter(val)}
          />
          <div className="relative">
            <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar jogador..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-3 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
            />
          </div>
        </div>

        {/* Ordenação */}
        <div className="flex gap-2">
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => setSortBy(opt.id)}
              className={`flex-1 py-2 text-xs font-bold rounded-xl transition-colors ${sortBy === opt.id ? 'bg-blue-600 text-white' : 'bg-white border border-slate-200 text-slate-500 hover:border-blue-300'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Filtro por posição */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {POSITION_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => setPositionFilter(opt.id)}
              className={`flex-shrink-0 px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${positionFilter === opt.id ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-500 hover:border-emerald-300'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Período */}
        {sortBy === 'galera' && (
          <div className="flex gap-2 bg-white border border-slate-200 rounded-xl p-1">
            {[{ id: 'all', label: 'Desde o início' }, { id: 'month', label: 'Último mês' }].map(opt => (
              <button
                key={opt.id}
                onClick={() => setPeriod(opt.id)}
                className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-colors ${period === opt.id ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-600'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Ranking */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : sortBy === 'torcida' ? (
          torcidaRanking.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <div className="text-3xl mb-2">⚽</div>
              <p className="text-sm">{cityFilter ? 'Nenhum craque encontrado por aqui ainda. Que tal convidar seus amigos e fazer a bola rolar?' : 'Nenhuma torcida encontrada.'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-slate-400 font-medium px-1 mb-1">
                <Trophy className="w-3.5 h-3.5 text-amber-400" />
                Torcidas mais representadas
              </div>
              {(() => {
                const maxCount = torcidaRanking[0]?.count || 1;
                return torcidaRanking.map((entry, i) => {
                  const isExpanded = expandedTeam === entry.team;
                  return (
                    <div key={entry.team} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                      <button className="w-full p-3 text-left" onClick={() => setExpandedTeam(isExpanded ? null : entry.team)}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-50 text-slate-400'}`}>{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-slate-700 text-sm truncate">{entry.team}</div>
                            <div className="text-[10px] text-slate-400">{entry.count} jogador{entry.count !== 1 ? 'es' : ''}</div>
                          </div>
                          {entry.avgRating != null && (
                            <div className="text-right shrink-0 mr-2">
                              <div className="font-bold text-slate-700 text-sm">★ {entry.avgRating.toFixed(1)}</div>
                              <div className="text-[9px] text-slate-400">média galera</div>
                            </div>
                          )}
                          {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-300 shrink-0" /> : <ChevronDown className="w-4 h-4 text-slate-300 shrink-0" />}
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${(entry.count / maxCount) * 100}%` }} />
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="border-t border-slate-50 divide-y divide-slate-50">
                          {entry.players.sort((a, b) => (b.communityRating ?? -1) - (a.communityRating ?? -1)).map(player => (
                            <button key={player.id} onClick={() => navigate(`/players/${player.id}`)} className="w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                              <PlayerAvatar user={player} size="xs" />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-slate-700 truncate">{player.name}</div>
                                <PositionBadge position={player.position} />
                              </div>
                              {player.communityRating != null && (
                                <div className="text-xs font-bold text-slate-500">★ {player.communityRating.toFixed(1)}</div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <div className="text-3xl mb-2">{cityFilter ? '⚽' : '🔍'}</div>
            <p className="text-sm">{cityFilter ? 'Nenhum craque encontrado por aqui ainda. Que tal convidar seus amigos e fazer a bola rolar?' : 'Nenhum jogador encontrado.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-slate-400 font-medium px-1 mb-1">
              <Trophy className="w-3.5 h-3.5 text-amber-400" />
              {sortBy === 'jogos' ? 'Por número de jogos' : sortBy === 'gols' ? 'Por gols marcados' : sortBy === 'assistencias' ? 'Por assistências' : 'Por nota da galera'}
            </div>
            <div className="lg:grid lg:grid-cols-2 lg:gap-3">
              {filtered.map((player, i) => (
                <button key={player.id} onClick={() => navigate(`/players/${player.id}`)} className="w-full text-left flex items-center gap-3 bg-white rounded-xl border border-slate-100 shadow-sm p-3 mb-2 lg:mb-0 hover:border-blue-200 hover:shadow-md transition-all">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-50 text-slate-400'}`}>
                    {i + 1}
                  </div>
                  <PlayerAvatar user={player} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-700 text-sm truncate flex items-center gap-1.5">
                      {player.name}
                      {player.heartTeam && player.heartTeam !== 'Sem Time' && (
                        <TeamFlag team={player.heartTeam} />
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <PositionBadge position={player.position} />
                      <span className="text-[10px] text-slate-400">{player.gameCount} jogo{player.gameCount !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {sortBy === 'jogos' ? (
                      <>
                        <div className="font-bold text-sm text-slate-700">{player.gameCount}</div>
                        <div className="text-[9px] text-slate-400 uppercase tracking-wide">jogos</div>
                      </>
                    ) : sortBy === 'gols' ? (
                      <>
                        <div className="font-bold text-sm text-emerald-700">⚽ {player.goalsCount}</div>
                        <div className="text-[9px] text-slate-400 uppercase tracking-wide">gols</div>
                      </>
                    ) : sortBy === 'assistencias' ? (
                      <>
                        <div className="font-bold text-sm text-blue-700">🅰️ {player.assistsCount}</div>
                        <div className="text-[9px] text-slate-400 uppercase tracking-wide">assist.</div>
                      </>
                    ) : (
                      <>
                        <div className={`font-bold text-sm ${player.communityRating != null ? 'text-slate-700' : 'text-slate-300'}`}>
                          {player.communityRating != null ? `★ ${player.communityRating.toFixed(1)}` : '—'}
                        </div>
                        <div className="text-[9px] text-slate-400 uppercase tracking-wide">
                          {period === 'month' ? (player.isMonthFallback ? 'geral (s/ dados)' : 'último mês') : 'nota galera'}
                        </div>
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CommunityPage;
